import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Search } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { countryBySlug, HUBS } from "../constants/options";
import { ExtraCard } from "../components/ExtraCard";
import { PricingCard } from "../components/PricingCard";

const q = (params) => `/search?${new URLSearchParams(params)}`;

const CountryHero = ({ c }) => {
  const [place, setPlace] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    navigate(place.trim() ? q({ country: c.name, near: place.trim(), radius: "25" }) : q({ country: c.name }));
  };
  return (
    <section className="relative overflow-hidden border-b border-white/10" data-testid="country-hero">
      <img src="/images/hero_background.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[hsl(0,0%,2%)] via-[hsl(0,0%,2%)]/80 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <p className="label-xs text-primary fade-up">Extras directory · {c.short}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold mt-5 max-w-4xl leading-[0.95] fade-up" style={{ animationDelay: "100ms" }} data-testid="country-title">
          TV & film extras in {c.the}
        </h1>
        <p className="mt-6 max-w-xl text-white/80 text-base md:text-lg fade-up" style={{ animationDelay: "180ms" }}>
          Find background artists, walk-ons and stand-ins near your shoot anywhere in {c.the}, or create your own page at extras.tv/yourname — first 6 months FREE, then {c.price} a year.
        </p>
        <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row max-w-xl border border-white/20 bg-black/60 backdrop-blur-xl fade-up" style={{ animationDelay: "260ms" }} data-testid="country-search-form">
          <input value={place} onChange={(e) => setPlace(e.target.value)} placeholder={c.slug === "ireland" ? "Town or city in Ireland" : `${c.postcodeLabel} or town in ${c.the}`} className="flex-1 bg-transparent px-5 py-4 outline-none placeholder:text-white/40 focus:ring-2 focus:ring-primary" data-testid="country-search-input" />
          <button className="bg-primary text-primary-foreground px-6 py-4 font-bold uppercase text-sm tracking-wider flex items-center justify-center gap-2 hover:bg-[hsl(43,100%,60%)] transition-colors" data-testid="country-search-button">
            <Search size={16} /> Find extras
          </button>
        </form>
      </div>
    </section>
  );
};

const Hubs = ({ c }) => (
  <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-12" data-testid="country-hubs">
    <div className="md:col-span-4">
      <p className="label-xs">Filming hubs</p>
      <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 leading-none">Extras near the busiest locations in {c.the}</h2>
    </div>
    <div className="md:col-span-8 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
      {c.cities.map((city) => (
        <Link key={city} to={q({ country: c.name, near: city, radius: "25" })} className="group bg-[hsl(0,0%,2%)] p-6 flex items-center justify-between hover:bg-card transition-colors" data-testid={`hub-${c.slug}-${city.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <span className="flex items-center gap-3"><MapPin size={16} className="text-primary" /><span className="font-display text-xl">Extras in {city}</span></span>
          <ArrowRight size={16} className="text-white/40 group-hover:text-primary group-hover:translate-x-1 transition-[transform,color]" />
        </Link>
      ))}
    </div>
  </section>
);

const Areas = ({ c }) => (
  <section className="max-w-7xl mx-auto px-6 md:px-10" data-testid="country-areas">
    <p className="label-xs">Browse by {c.regionLabel.toLowerCase()}</p>
    <div className="flex flex-wrap gap-2 mt-6">
      {c.regions.map((r) => (
        <Link key={r} to={q({ country: c.name, region: r })} className={`border px-3 py-1.5 text-sm hover:border-primary hover:text-primary transition-colors ${HUBS[c.name].includes(r) ? "border-white/30" : "border-white/10 text-white/60"}`} data-testid={`area-${c.slug}-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          {r}
        </Link>
      ))}
    </div>
  </section>
);

const LocalExtras = ({ c }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/extras", { params: { country: c.name, limit: 8 } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [c.name]);
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 pt-24" data-testid="country-extras">
      <div className="flex items-end justify-between">
        <p className="label-xs">Extras in {c.the}</p>
        <Link to={q({ country: c.name })} className="text-primary text-sm font-bold uppercase tracking-wider" data-testid="country-see-all">See all</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6">
        {items.map((e, i) => <ExtraCard key={e.slug} extra={e} index={i} />)}
      </div>
    </section>
  );
};

const Join = ({ c }) => (
  <section className="max-w-7xl mx-auto px-6 md:px-10 pt-24 grid md:grid-cols-12 gap-12 items-center" data-testid="country-join">
    <div className="md:col-span-6">
      <p className="label-xs">Become an extra in {c.the}</p>
      <h2 className="font-display text-3xl md:text-5xl font-bold mt-4 leading-none">Get seen by productions filming near you</h2>
      <p className="text-white/60 mt-6 max-w-md">Create your profile with photos, your {c.regionLabel.toLowerCase()}, playing age and skills. Casting teams search by distance, so being listed in your area matters.</p>
    </div>
    <div className="md:col-span-5 md:col-start-8">
      <PricingCard price={c.price} testId="country-pricing-card">
        <Link to="/register" className="block text-center bg-primary text-primary-foreground py-4 font-bold uppercase tracking-wider text-sm hover:bg-[hsl(43,100%,60%)] transition-colors" data-testid="country-join-button">Get listed</Link>
      </PricingCard>
    </div>
  </section>
);

export default function CountryPage({ slug }) {
  const params = useParams();
  const c = countryBySlug(slug || params.country);
  useSeo(
    c ? `TV & Film Extras in ${c.the} – Find Background Artists | extras.tv` : "extras.tv",
    c ? `Find TV and film extras near your shoot in ${c.cities.slice(0, 4).join(", ")} and across ${c.the}. Extras: list your profile free for 6 months, then ${c.price} a year.` : ""
  );
  if (!c) return <Navigate to="/" replace />;
  return (
    <div data-testid={`country-page-${c.slug}`}>
      <CountryHero c={c} />
      <Hubs c={c} />
      <Areas c={c} />
      <LocalExtras c={c} />
      <Join c={c} />
    </div>
  );
}
